import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Live computed preview given a rule_type + rule_value.
 *
 * Uses a hardcoded mock agent for demonstration:
 *   - last_month_value: 4,500,000 (e.g. diamonds)
 *   - tier (sample): 3
 *
 * Returns a human-readable string describing what target value the rule
 * would produce.
 */
const SAMPLE_AGENT = {
  diamonds_last_month: 4_500_000,
  recruited_last_month: 8,
  sample_tier: 3,
  tier_population: 5,
};

const formatNumber = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Math.round(n).toLocaleString();
};

const RulePreview = ({ ruleType, ruleValue, metric }) => {
  const { t } = useTranslation();

  const result = useMemo(() => {
    if (!ruleType) return null;
    const isDiamonds = String(metric || '').toLowerCase().includes('diamond');
    const lmValue = isDiamonds
      ? SAMPLE_AGENT.diamonds_last_month
      : SAMPLE_AGENT.recruited_last_month;

    switch (ruleType) {
      case 'flat': {
        const v = Number(ruleValue?.value);
        if (!Number.isFinite(v)) return null;
        return {
          line: t(
            'admin.objectiveRules.preview.flat',
            'Target = {{value}}',
            { value: formatNumber(v) }
          ),
          context: t(
            'admin.objectiveRules.preview.flatContext',
            'Each agent gets exactly {{value}} for {{metric}}.',
            { value: formatNumber(v), metric: metric || '—' }
          ),
        };
      }
      case 'lm_growth_pct': {
        const pct = Number(ruleValue?.pct);
        if (!Number.isFinite(pct)) return null;
        const target = lmValue * (1 + pct / 100);
        return {
          line: t(
            'admin.objectiveRules.preview.lmGrowth',
            'Target = M-1 × (1 + {{pct}}%) = {{value}}',
            { pct, value: formatNumber(target) }
          ),
          context: t(
            'admin.objectiveRules.preview.lmGrowthContext',
            'Sample: M-1 = {{lm}} → target = {{value}}',
            { lm: formatNumber(lmValue), value: formatNumber(target) }
          ),
        };
      }
      case 'lm_value': {
        return {
          line: t(
            'admin.objectiveRules.preview.lmValue',
            'Target = M-1 value (= {{value}})',
            { value: formatNumber(lmValue) }
          ),
          context: t(
            'admin.objectiveRules.preview.lmValueContext',
            'Each agent inherits their own previous-month value.'
          ),
        };
      }
      case 'tier_based': {
        const perTier = ruleValue?.per_tier || {};
        const sampleVal =
          perTier[SAMPLE_AGENT.sample_tier] ??
          perTier[String(SAMPLE_AGENT.sample_tier)];
        if (sampleVal === undefined || sampleVal === null) {
          return {
            line: t(
              'admin.objectiveRules.preview.tierBasedNoSample',
              'Sample tier T{{tier}} not configured.',
              { tier: SAMPLE_AGENT.sample_tier }
            ),
            context: t(
              'admin.objectiveRules.preview.tierBasedContext',
              'Each agent gets the value matching their tier.'
            ),
          };
        }
        return {
          line: t(
            'admin.objectiveRules.preview.tierBased',
            'Sample tier T{{tier}} → target = {{value}}',
            { tier: SAMPLE_AGENT.sample_tier, value: formatNumber(sampleVal) }
          ),
          context: t(
            'admin.objectiveRules.preview.tierBasedContext',
            'Each agent gets the value matching their tier.'
          ),
        };
      }
      case 'upgrade_pct_of_tier':
      case 'maintain_pct_of_tier': {
        const pct = Number(ruleValue?.value);
        if (!Number.isFinite(pct)) return null;
        const expected = (SAMPLE_AGENT.tier_population * pct) / 100;
        const labelKey =
          ruleType === 'maintain_pct_of_tier'
            ? 'admin.objectiveRules.preview.maintainPctOfTier'
            : 'admin.objectiveRules.preview.upgradePctOfTier';
        const labelFallback =
          ruleType === 'maintain_pct_of_tier'
            ? '{{pct}}% of {{population}} creators in T{{tier}} should maintain → ≈ {{expected}}'
            : '{{pct}}% of {{population}} creators in T{{tier}} should rank-up → ≈ {{expected}}';
        return {
          line: t(labelKey, labelFallback, {
            pct,
            population: SAMPLE_AGENT.tier_population,
            tier: SAMPLE_AGENT.sample_tier,
            expected: formatNumber(expected),
          }),
          context: t(
            'admin.objectiveRules.preview.pctOfTierContext',
            'Applied to every tier of every agent during regeneration.'
          ),
        };
      }
      case 'upgrade_count_per_tier':
      case 'maintain_count_per_tier': {
        const perTier = ruleValue?.per_tier || {};
        const sampleVal =
          perTier[SAMPLE_AGENT.sample_tier] ??
          perTier[String(SAMPLE_AGENT.sample_tier)];
        if (sampleVal === undefined || sampleVal === null) {
          return {
            line: t(
              'admin.objectiveRules.preview.tierBasedNoSample',
              'Sample tier T{{tier}} not configured.',
              { tier: SAMPLE_AGENT.sample_tier }
            ),
            context: t(
              'admin.objectiveRules.preview.countPerTierContext',
              'Per-tier count target.'
            ),
          };
        }
        const labelKey =
          ruleType === 'maintain_count_per_tier'
            ? 'admin.objectiveRules.preview.maintainCount'
            : 'admin.objectiveRules.preview.upgradeCount';
        const fallback =
          ruleType === 'maintain_count_per_tier'
            ? 'T{{tier}} → {{count}} creators expected to maintain'
            : 'T{{tier}} → {{count}} creators expected to rank-up';
        return {
          line: t(labelKey, fallback, {
            tier: SAMPLE_AGENT.sample_tier,
            count: formatNumber(sampleVal),
          }),
          context: t(
            'admin.objectiveRules.preview.countPerTierContext',
            'Per-tier count target.'
          ),
        };
      }
      default:
        return null;
    }
  }, [ruleType, ruleValue, metric, t]);

  return (
    <div className="rounded-xl border border-accent/30 bg-accent/5 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-accent font-semibold mb-1">
        {t('admin.objectiveRules.preview.title', 'Live preview')}
      </div>
      {result ? (
        <>
          <div className="text-sm font-medium text-foreground tabular-nums">
            {result.line}
          </div>
          {result.context && (
            <p className="text-xs text-muted-foreground mt-1">{result.context}</p>
          )}
        </>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          {t(
            'admin.objectiveRules.preview.empty',
            'Fill in the form above to see the computed target.'
          )}
        </div>
      )}
    </div>
  );
};

export default RulePreview;
